// ===========================================
// Google Drive Connection Component
// ===========================================
// Displays connection status and OAuth connect/disconnect buttons

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Skeleton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ArrowBack as BackIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import {
  Cloud as CloudIcon,
  CloudOff as CloudOffIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  useGoogleDriveAuthStatus,
  useGoogleDriveAuthUrl,
  useRevokeGoogleDriveAccess,
  useSetRootFolder,
  useBrowseFolders,
} from '../../hooks/useGoogleDrive';
import ConfirmDialog from '../common/ConfirmDialog';
import { usePermissions } from '../../hooks/usePermissions';
import { useOwnerTitle } from '../../hooks/useOwnerTitle';
import { DriveFolder } from '../../api/googleDrive.api';

// ===========================================
// COMPONENT
// ===========================================

export default function GoogleDriveConnection() {
  // Connect, disconnect and the root folder need integrations.write (roles plan 5.6).
  const { can } = usePermissions();
  const ownerTitle = useOwnerTitle();
  const canManage = can('integrations', 'write');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showRootFolderDialog, setShowRootFolderDialog] = useState(false);
  const [browsePath, setBrowsePath] = useState<{ id: string | null; name: string }[]>([
    { id: null, name: 'My Drive' },
  ]);
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [isProcessingCallback, setIsProcessingCallback] = useState(false);

  // Current folder for browsing (null = root of Drive, not the configured root folder)
  const currentFolderId = browsePath.length > 1 ? browsePath[browsePath.length - 1].id : null;

  // Queries
  const { data: authStatus, isLoading: isLoadingStatus } = useGoogleDriveAuthStatus();
  const getAuthUrlMutation = useGoogleDriveAuthUrl();
  const revokeMutation = useRevokeGoogleDriveAccess();
  const setRootFolderMutation = useSetRootFolder();

  // Fetch folders for root folder picker (bypass configured root for this query)
  const { data: folders, isLoading: isLoadingFolders } = useBrowseFolders(
    { parentId: currentFolderId || undefined },
    { enabled: showRootFolderDialog }
  );

  const isConnected = authStatus?.isConnected ?? false;
  const rootFolderName = authStatus?.rootFolderName;
  const rootFolderId = authStatus?.rootFolderId;

  // Handle URL params from OAuth callback
  useEffect(() => {
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');

    if (connected === 'true') {
      // Show processing state immediately
      setIsProcessingCallback(true);

      setStatusMessage({
        type: 'success',
        message: 'Google Drive connected successfully!',
      });

      // Clear the URL params and hide processing state
      searchParams.delete('connected');
      setSearchParams(searchParams, { replace: true });

      // Keep processing state for a brief moment to allow hooks to refetch
      setTimeout(() => {
        setIsProcessingCallback(false);
      }, 500);
    } else if (error) {
      let errorMessage = 'Failed to connect Google Drive';
      switch (error) {
        case 'access_denied':
          errorMessage = 'Access was denied. Please try again and grant permission.';
          break;
        case 'invalid_state':
          errorMessage = 'Invalid session. Please try connecting again.';
          break;
        case 'auth_failed':
          errorMessage = 'Authentication failed. Please try again.';
          break;
        case 'expired_code':
          errorMessage = 'The authorisation link expired. Please click Connect again.';
          break;
        case 'not_permitted':
          errorMessage = "You don't have permission to connect the school's Google Drive.";
          break;
        case 'missing_params':
          errorMessage = 'Missing authorization parameters. Please try again.';
          break;
        default:
          errorMessage = `Connection error: ${error}`;
      }
      setStatusMessage({
        type: 'error',
        message: errorMessage,
      });
      // Clear the URL params
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle connect button click
  const handleConnect = async () => {
    try {
      const result = await getAuthUrlMutation.mutateAsync();
      // Redirect to Google OAuth
      window.location.href = result.authUrl;
    } catch {
      // Error is handled by the mutation
    }
  };

  // Handle disconnect confirmation
  const handleDisconnect = async () => {
    try {
      await revokeMutation.mutateAsync();
      setShowDisconnectDialog(false);
      setStatusMessage({
        type: 'success',
        message: 'Google Drive disconnected successfully.',
      });
    } catch {
      // Error is handled by the mutation
    }
  };

  // Handle folder navigation in picker
  const handleFolderClick = (folder: DriveFolder) => {
    setBrowsePath([...browsePath, { id: folder.id, name: folder.name }]);
  };

  // Handle back navigation
  const handleBackClick = () => {
    if (browsePath.length > 1) {
      setBrowsePath(browsePath.slice(0, -1));
    }
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (index: number) => {
    setBrowsePath(browsePath.slice(0, index + 1));
  };

  // Handle selecting current folder as root
  const handleSelectCurrentFolder = async () => {
    const currentFolder = browsePath[browsePath.length - 1];
    await setRootFolderMutation.mutateAsync({
      rootFolderId: currentFolder.id,
      rootFolderName: currentFolder.name,
    });
    setShowRootFolderDialog(false);
    setBrowsePath([{ id: null, name: 'My Drive' }]);
  };

  // Handle clearing root folder
  const handleClearRootFolder = async () => {
    await setRootFolderMutation.mutateAsync({
      rootFolderId: null,
      rootFolderName: null,
    });
    setShowRootFolderDialog(false);
  };

  // Reset browse path when dialog opens
  const handleOpenRootFolderDialog = () => {
    setBrowsePath([{ id: null, name: 'My Drive' }]);
    setShowRootFolderDialog(true);
  };

  // Loading state (also show when processing OAuth callback)
  if (isLoadingStatus || isProcessingCallback) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">
                {isProcessingCallback
                  ? 'Connecting to Google Drive...'
                  : 'Loading connection status...'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isProcessingCallback
                  ? 'Please wait while we complete the connection.'
                  : 'This will only take a moment.'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Status Message */}
      {statusMessage && (
        <Alert
          severity={statusMessage.type}
          onClose={() => setStatusMessage(null)}
          sx={{ mb: 2 }}
          icon={statusMessage.type === 'success' ? <CheckIcon /> : <ErrorIcon />}
        >
          {statusMessage.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            {/* Connection Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isConnected ? 'success.light' : 'grey.200',
                }}
              >
                {isConnected ? (
                  <CloudIcon sx={{ color: 'success.main', fontSize: 28 }} />
                ) : (
                  <CloudOffIcon sx={{ color: 'grey.500', fontSize: 28 }} />
                )}
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6">Google Drive</Typography>
                  <Chip
                    label={isConnected ? 'Connected' : 'Not Connected'}
                    color={isConnected ? 'success' : 'default'}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {isConnected
                    ? 'Your school is connected to Google Drive. Files will sync automatically.'
                    : 'Connect to Google Drive to sync files and folders with your lessons.'}
                </Typography>
              </Box>
            </Box>

            {/* Action Button */}
            <Box>
              {!canManage ? (
                !isConnected && (
                  <Typography variant="body2" color="text.secondary">
                    Ask your {ownerTitle} or someone with Integrations edit access to connect Google Drive.
                  </Typography>
                )
              ) : isConnected ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => setShowDisconnectDialog(true)}
                  disabled={revokeMutation.isPending}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={
                    getAuthUrlMutation.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <LinkIcon />
                    )
                  }
                  onClick={handleConnect}
                  disabled={getAuthUrlMutation.isPending}
                >
                  {getAuthUrlMutation.isPending ? 'Connecting...' : 'Connect Google Drive'}
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Root Folder Settings (only when connected) */}
      {isConnected && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: rootFolderId ? 'primary.light' : 'grey.200',
                  }}
                >
                  <FolderOpenIcon sx={{ color: rootFolderId ? 'primary.main' : 'grey.500', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography variant="h6">Root Folder</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {rootFolderId
                      ? `Browsing restricted to: ${rootFolderName}`
                      : 'No restriction - browsing from entire Drive'}
                  </Typography>
                </Box>
              </Box>

              {canManage && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                {rootFolderId && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleClearRootFolder}
                    disabled={setRootFolderMutation.isPending}
                  >
                    Clear
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={handleOpenRootFolderDialog}
                  disabled={setRootFolderMutation.isPending}
                >
                  {rootFolderId ? 'Change' : 'Set Root Folder'}
                </Button>
              </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Disconnect Confirmation Dialog */}
      <ConfirmDialog
        open={showDisconnectDialog}
        title="Disconnect Google Drive"
        message="Are you sure you want to disconnect Google Drive? Synced files will remain in the portal, but automatic syncing will stop. You can reconnect at any time."
        confirmLabel="Disconnect"
        confirmColor="error"
        loading={revokeMutation.isPending}
        onConfirm={handleDisconnect}
        onCancel={() => setShowDisconnectDialog(false)}
      />

      {/* Root Folder Picker Dialog */}
      <Dialog
        open={showRootFolderDialog}
        onClose={() => setShowRootFolderDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Root Folder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a folder to restrict browsing. Teachers and other users will only see folders and files within this folder.
          </Typography>

          {/* Breadcrumb navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {browsePath.length > 1 && (
              <Button
                size="small"
                startIcon={<BackIcon />}
                onClick={handleBackClick}
              >
                Back
              </Button>
            )}
            <Breadcrumbs>
              {browsePath.map((item, index) => (
                <Button
                  key={index}
                  size="small"
                  onClick={() => handleBreadcrumbClick(index)}
                  disabled={index === browsePath.length - 1}
                  startIcon={index === 0 ? <HomeIcon fontSize="small" /> : undefined}
                  sx={{ textTransform: 'none', minWidth: 'auto' }}
                >
                  {item.name}
                </Button>
              ))}
            </Breadcrumbs>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Folder list */}
          {isLoadingFolders ? (
            <Box sx={{ py: 2 }}>
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1 }} />
              ))}
            </Box>
          ) : folders && folders.length > 0 ? (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {folders.map((folder) => (
                <ListItemButton
                  key={folder.id}
                  onClick={() => handleFolderClick(folder)}
                >
                  <ListItemIcon>
                    <FolderIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary={folder.name} />
                </ListItemButton>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No subfolders in this location
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRootFolderDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSelectCurrentFolder}
            disabled={setRootFolderMutation.isPending}
          >
            {setRootFolderMutation.isPending
              ? 'Saving...'
              : `Select "${browsePath[browsePath.length - 1].name}"`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
